import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowUpFromLine, ExternalLink, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { AdminPage, ConfirmActionDialog, DESTRUCTIVE_OUTLINE, EmptyState, KpiCard, formatNumber } from '../../../components/design-system/admin';
import { networkErrorMessage, useNetworkAdminApi } from '../../../common/hooks/use-network-admin-api.hook';
import { NETWORK_BRAND_STATUSES, type BrandDetail } from '../../../common/hooks/network-admin.types';
import type { NetworkBrand, NetworkBrandStatus } from '../../../common/models/network.model';
import { AliasesEditor, BrandDetailsForm, CollectionsCard } from '../../../modules/superadmin/network/BrandEditors';
import { BrandProductsGrid } from '../../../modules/superadmin/network/BrandProductsGrid';
import { ProductEditDialog } from '../../../modules/superadmin/network/ProductEditDialog';
import { PromoteProductDialog } from '../../../modules/superadmin/network/PromoteProductDialog';
import { ErrorNote } from '../../../modules/superadmin/network/network-parts';
import { vocabKey } from '../../../modules/superadmin/network/network-helpers';

/**
 * One manufacturer: its status in the network, the names retailers know it by,
 * its collections and its products — and the two ways products get in by hand
 * (create one, or promote a retailer's product).
 */
export function NetworkBrandPage() {
    const { t } = useTranslation();
    const { id = '' } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const api = useNetworkAdminApi();
    const [detail, setDetail] = useState<BrandDetail | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [statusBusy, setStatusBusy] = useState(false);
    const [promoting, setPromoting] = useState(false);
    const [creating, setCreating] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [productsKey, setProductsKey] = useState(0);

    const load = useCallback(async () => {
        setError(null);
        try {
            const d = await api.brand(id);
            if (!d?.brand) throw new Error(t('ADMIN_NETWORK.ERRORS.NO_RESPONSE'));
            setDetail(d);
        } catch (e) {
            setError(networkErrorMessage(e) || t('ADMIN_SHELL.COMMON.LOAD_FAILED'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, id]);

    useEffect(() => {
        setDetail(null);
        load();
    }, [load]);

    const onBrandSaved = (brand: NetworkBrand) => setDetail((d) => (d ? { ...d, brand: { ...d.brand, ...brand } } : d));

    const changeStatus = async (status: NetworkBrandStatus) => {
        if (!detail) return;
        const before = detail.brand.status;
        setStatusBusy(true);
        onBrandSaved({ ...detail.brand, status });
        try {
            const saved = await api.updateBrand(detail.brand._id, { status });
            if (saved?._id) onBrandSaved(saved);
            toast.success(t('ADMIN_NETWORK.BRAND.STATUS_SAVED'));
        } catch (e) {
            onBrandSaved({ ...detail.brand, status: before });
            toast.error(networkErrorMessage(e) || t('ADMIN_NETWORK.ERRORS.GENERIC'));
        } finally {
            setStatusBusy(false);
        }
    };

    const remove = async () => {
        if (!detail) return;
        setDeleteBusy(true);
        setDeleteError(null);
        try {
            await api.deleteBrand(detail.brand._id);
            setDeleting(false);
            toast.success(t('ADMIN_NETWORK.BRAND.DELETED'));
            navigate('/network/brands');
        } catch (e) {
            setDeleteError(networkErrorMessage(e) || t('ADMIN_NETWORK.ERRORS.GENERIC'));
        } finally {
            setDeleteBusy(false);
        }
    };

    const parents = [{ label: t('ADMIN_NETWORK.NAV'), href: '/network' }, { label: t('ADMIN_NETWORK.NAV_BRANDS'), href: '/network/brands' }];
    const brand = detail?.brand;
    const counts = detail?.counts || {};

    if (error && !detail) {
        return (
            <AdminPage title={t('ADMIN_NETWORK.BRAND.TITLE')} parents={parents}>
                <EmptyState
                    title={t('ADMIN_SHELL.COMMON.LOAD_FAILED')}
                    description={error}
                    action={<Button variant="outline" onClick={load}>{t('ADMIN_SHELL.COMMON.RETRY')}</Button>}
                    className="py-24"
                />
            </AdminPage>
        );
    }

    return (
        <AdminPage
            title={brand?.name || t('ADMIN_NETWORK.BRAND.TITLE')}
            parents={parents}
            header={
                <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                    <div className="min-w-0">
                        {brand ? (
                            <>
                                <h1 className="font-heading text-2xl font-medium tracking-tight">{brand.name}</h1>
                                <p className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
                                    <span>{[brand.country, brand.slug].filter(Boolean).join(' · ')}</span>
                                    {brand.website && (
                                        <a href={brand.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-foreground" dir="ltr">
                                            {brand.website.replace(/^https?:\/\//, '')}
                                            <ExternalLink className="size-3" />
                                        </a>
                                    )}
                                </p>
                            </>
                        ) : (
                            <Skeleton className="h-8 w-56" />
                        )}
                    </div>
                    {brand && (
                        <div className="flex flex-wrap items-center gap-2">
                            <NativeSelect
                                value={brand.status}
                                onChange={(e) => changeStatus(e.target.value as NetworkBrandStatus)}
                                disabled={statusBusy}
                                aria-label={t('ADMIN_NETWORK.FIELDS.STATUS')}
                                data-testid="brand-status"
                            >
                                {NETWORK_BRAND_STATUSES.map((s) => (
                                    <NativeSelectOption key={s} value={s}>{t(`ADMIN_NETWORK.BRAND_STATUS.${vocabKey(s)}`)}</NativeSelectOption>
                                ))}
                            </NativeSelect>
                            <Button variant="outline" onClick={() => setCreating(true)} data-testid="new-product">
                                <Plus />
                                {t('ADMIN_NETWORK.PRODUCT_EDIT.TITLE_CREATE')}
                            </Button>
                            <Button variant="outline" onClick={() => setPromoting(true)} data-testid="promote-open">
                                <ArrowUpFromLine />
                                {t('ADMIN_NETWORK.PROMOTE.OPEN')}
                            </Button>
                            <Button
                                variant="outline"
                                className={DESTRUCTIVE_OUTLINE}
                                onClick={() => {
                                    setDeleteError(null);
                                    setDeleting(true);
                                }}
                                data-testid="brand-delete"
                            >
                                <Trash2 />
                                {t('ADMIN_NETWORK.BRAND.DELETE')}
                            </Button>
                        </div>
                    )}
                </div>
            }
        >
          {/* `contents`: a hook for tests that leaves the page's own layout alone. */}
          <div className="contents" data-testid="brand-detail">
            {!detail ? (
                <div className="grid gap-4">
                    <Skeleton className="h-24 w-full rounded-xl" />
                    <Skeleton className="h-64 w-full rounded-xl" />
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="brand-kpis">
                        <KpiCard label={t('ADMIN_NETWORK.COVERAGE.PRODUCTS')} value={formatNumber(counts.products ?? 0)} hint={t('ADMIN_NETWORK.BRAND.PUBLISHED_HINT', { count: counts.published ?? 0 })} />
                        <KpiCard label={t('ADMIN_NETWORK.COVERAGE.COLLECTIONS')} value={formatNumber(counts.collections ?? detail.collections.length)} />
                        <KpiCard label={t('ADMIN_NETWORK.COVERAGE.RETAILERS')} value={formatNumber(counts.retailers ?? 0)} hint={t('ADMIN_NETWORK.BRAND.RETAILERS_HINT')} />
                        <KpiCard label={t('ADMIN_NETWORK.FIELDS.STATUS')} value={t(`ADMIN_NETWORK.BRAND_STATUS.${vocabKey(detail.brand.status)}`)} hint={detail.brand.vendor ? t('ADMIN_NETWORK.BRAND.HAS_ACCOUNT') : t('ADMIN_NETWORK.BRAND.NO_ACCOUNT')} />
                    </div>

                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                        <BrandDetailsForm brand={detail.brand} api={api} onSaved={onBrandSaved} />
                        <AliasesEditor brand={detail.brand} api={api} onSaved={onBrandSaved} />
                        <CollectionsCard brandId={detail.brand._id} collections={detail.collections} api={api} onChanged={load} />
                    </div>

                    <BrandProductsGrid brandId={detail.brand._id} collections={detail.collections} api={api} reloadKey={productsKey} onChanged={load} />

                    <PromoteProductDialog
                        open={promoting}
                        onOpenChange={setPromoting}
                        brandId={detail.brand._id}
                        brandName={detail.brand.name}
                        collections={detail.collections}
                        onPromoted={(p) => {
                            toast.success(t('ADMIN_NETWORK.PROMOTE.DONE'));
                            navigate(`/network/products/${p._id}`);
                        }}
                    />
                    <ProductEditDialog
                        open={creating}
                        onOpenChange={setCreating}
                        mode="create"
                        brandId={detail.brand._id}
                        collections={detail.collections}
                        onSaved={(p) => {
                            setProductsKey((k) => k + 1);
                            load();
                            navigate(`/network/products/${p._id}`);
                        }}
                    />
                    <ConfirmActionDialog
                        open={deleting}
                        onOpenChange={setDeleting}
                        title={t('ADMIN_NETWORK.BRAND.DELETE_TITLE', { name: detail.brand.name })}
                        description={t('ADMIN_NETWORK.BRAND.DELETE_BODY')}
                        confirmLabel={t('ADMIN_SHELL.COMMON.DELETE')}
                        destructive
                        busy={deleteBusy}
                        onConfirm={remove}
                    >
                        {deleteError && <ErrorNote message={deleteError} />}
                    </ConfirmActionDialog>
                </>
            )}
          </div>
        </AdminPage>
    );
}

export default NetworkBrandPage;
